import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet as WalletIcon, Plus } from "lucide-react";
import { useAccount, publicApi } from "@/lib/account";
import { payWithRazorpay, recoverPayments } from "@/lib/razorpay";
import { usePrefs } from "@/lib/prefs";

export default function Wallet() {
  const { account, authed, refresh, profile } = useAccount();
  const { local } = usePrefs();
  const [topups, setTopups] = useState([]);
  const [history, setHistory] = useState([]);
  const load = () => authed("get", "/payments/history").then(setHistory).catch(() => {});
  useEffect(() => { publicApi.get("/plans").then((r) => setTopups(r.data.topups)); load(); recoverPayments(authed, () => { refresh(); load(); }); }, []); // eslint-disable-line

  const topup = (amount) => payWithRazorpay({ authed, payload: { kind: "topup", amount }, description: `Wallet top-up ₹${amount}`,
    prefillName: profile?.name, onSuccess: () => { refresh(); load(); } });

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14" data-testid="wallet-page">
      <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Wallet</p>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3">Your chat wallet</h1>
      <div className="grid md:grid-cols-5 gap-6 mt-10">
        <div className="md:col-span-2 rounded-3xl p-8 bg-gradient-to-br from-[#3A2410] via-[#1C1410] to-[#181B26] border border-amber-500/40 relative overflow-hidden grain">
          <WalletIcon className="w-8 h-8 text-amber-300" />
          <p className="text-sm text-slate-300 mt-8">Balance</p>
          <p className="font-display text-6xl font-semibold gold-text mt-1" data-testid="wallet-balance-amount">₹{account?.balance ?? 0}</p>
          <p className="text-sm text-slate-400 mt-2">{local(account?.balance ?? 0) || "Chat credit"} · ₹1 local / ₹30 cross-border message</p>
          <p className="text-xs mt-6 text-slate-400" data-testid="wallet-plan-status">
            {account?.plan_active ? `${account.plan} plan active until ${new Date(account.plan_expires).toLocaleDateString("en-IN")}` : <>No active plan · <Link to="/plans" className="text-amber-300 underline">Subscribe</Link></>}
          </p>
        </div>
        <div className="md:col-span-3 card-royal rounded-3xl p-8">
          <h2 className="font-display text-2xl">Top up</h2>
          <p className="text-sm text-slate-400 mt-1">₹1 per message in your country, ₹30 per cross-border message. Pay with UPI, card or netbanking.</p>
          <div className="grid grid-cols-2 gap-4 mt-6">
            {topups.map((a) => (
              <button key={a} onClick={() => topup(a)} data-testid={`wallet-topup-${a}-button`} className="btn-ghost rounded-2xl py-5 flex items-center justify-center gap-2 text-lg">
                <Plus className="w-4 h-4" /> ₹{a}{local(a) && <span className="text-xs text-slate-400">{local(a)}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="card-royal rounded-3xl p-8 mt-6" data-testid="wallet-history">
        <h2 className="font-display text-2xl">Payment history</h2>
        {history.length === 0 ? <p className="text-sm text-slate-500 mt-4">No payments yet.</p> : (
          <div className="mt-4 divide-y divide-slate-800">
            {history.map((h) => (
              <div key={h.order_id} className="py-3 flex justify-between text-sm">
                <span>{h.kind === "plan" ? `${h.plan_id} plan` : "Wallet top-up"}</span>
                <span className="text-slate-400">{new Date(h.created_at).toLocaleDateString("en-IN")}</span>
                <span className="text-amber-300">₹{h.amount}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
